// Shared by email/files. A lost response is not evidence that a write failed.
export class ProviderWriteAttempt {
  mayHaveWritten = false;

  async run<T>(operation: () => Promise<T>): Promise<T> {
    const earlierWrite = this.mayHaveWritten;
    this.mayHaveWritten = true;
    try {
      return await operation();
    } catch (error) {
      // Only an explicit rejection of the FIRST mutation permits a retry.
      // Timeouts/5xx/parse errors, or failure after a prior mutation, stay pending.
      const code = error instanceof Error ? error.message : "";
      if (!earlierWrite && /^provider_http_4\d\d$/.test(code) && code !== "provider_http_408") {
        this.mayHaveWritten = false;
      }
      throw error;
    }
  }
}

export function hasConnectorPaidAccess(subscription: { plan?: unknown; current_period_end?: unknown } | null): boolean {
  if (!subscription || !["premium", "pro", "ultra"].includes(String(subscription.plan))) return false;
  if (typeof subscription.current_period_end !== "string") return false;
  const end = subscription.current_period_end.replace(" ", "T")
    .replace(/([+-])(\d{2})(\d{2})$/, "$1$2:$3").replace(/([+-])(\d{2})$/, "$1$2:00");
  return Date.parse(end) > Date.now();
}

function canonical(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonical);
  if (value && typeof value === "object") {
    return Object.fromEntries(Object.entries(value).sort(([a], [b]) => a.localeCompare(b))
      .map(([key, item]) => [key, canonical(item)]));
  }
  return value;
}

export async function connectorRequestHash(body: Record<string, unknown>): Promise<string> {
  const { idempotency_key: _key, user_id: _user, ...payload } = body;
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(JSON.stringify(canonical(payload))));
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

export interface ConnectorReceipt {
  id: string;
  provider: string;
  capability: string;
  status: string;
  result?: { request_hash?: string; [key: string]: unknown } | null;
  external_id?: string | null;
  error_code?: string | null;
}

export function receiptMatches(receipt: ConnectorReceipt, provider: string, action: string, hash: string): boolean {
  return receipt.provider === provider && receipt.capability === action && receipt.result?.request_hash === hash;
}

export function canRetryReceipt(receipt: ConnectorReceipt): boolean {
  // Legacy failures have unknown outcomes; never reclaim them automatically.
  return receipt.status === "failed" && receipt.error_code?.startsWith("safe_retry:") === true;
}

export function canRetargetDocumentReceipt(receipt: ConnectorReceipt, key: string, action: string): boolean {
  // A reviewed document may switch between binary storage and a Notion page
  // only after an explicit no-write failure. Keep its original receipt key.
  const documentActions = ["upload_file", "create_text"];
  return /^document:[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(key)
    && documentActions.includes(action) && documentActions.includes(receipt.capability)
    && canRetryReceipt(receipt);
}
